import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useCallback, useEffect, useRef, useState } from 'react';
import { createErrorResult, createSuccessResult, isException, isSuccessful } from '@atlassian/commerce-resultful';
import { ProbabilityOfBug } from '@atlassian/commerce-telemetry';
import { useTaskTracker } from './common/utils/events';
import { useCallbackPromiseQueue } from './controllers/use-callback-promise-queue';
import { useReCaptchaService } from './services/recaptcha';
import { shouldSkipReCaptchaChallenge, useSiteKeyService } from './services/site-key';
export { FAILED_SITE_KEY_RETRIEVAL } from './services/site-key';
export { loadEnterpriseReCaptchaScript, loadFreeReCaptchaScript } from './services/recaptcha';
export { isSuccessful, isFailure, isException, isError } from '@atlassian/commerce-resultful';
export { ReCaptchaErrorMessage, ReCaptchaLegalText, RawReCaptchaLegalText } from './ui';
export { ReCaptchaStateProvider } from './controllers/recaptcha-state-provider';

/**
 * A low level alternative to {@link useCaptchaChallengeEventHandler}. The difference is that this
 * callback requires a HTML element to be passed in rather than an event.
 *
 * Note: In order to avoid introducing bugs into reCAPTCHA, please understand the various assumptions
 * required in order to handle reCAPTCHA edge cases (see reCAPTCHA documents).
 *
 * @param fetchSiteKeyProgress Will be called when this hook needs to retrieve a reCAPTCHA site key.
 * @param initializeOptions Customizable settings for this hook
 * @returns A result with the reCAPTCHA token inside of it
 */
export var useRequestCaptchaChallenge = function useRequestCaptchaChallenge(fetchSiteKeyProgress, loadReCaptchaOptions) {
  var siteKeyService = useSiteKeyService(fetchSiteKeyProgress);
  var reCaptchaService = useReCaptchaService(loadReCaptchaOptions);
  var startTask = useTaskTracker({
    action: 'submit',
    actionSubject: 'reCaptcha'
  });
  var requestCaptchaChallenge = useCallbackPromiseQueue(useCallback(function (element, challengeOptions) {
    if (siteKeyService.loading) {
      return;
    }

    if (siteKeyService.value.failed) {
      return {
        resolveWith: Promise.resolve(createErrorResult(siteKeyService.value))
      };
    }

    if (shouldSkipReCaptchaChallenge(siteKeyService.value.siteKey)) {
      return {
        resolveWith: Promise.resolve(createSuccessResult(null))
      };
    }

    if (reCaptchaService.loading) {
      return;
    }

    var resultPromise = reCaptchaService.value.requestCaptchaChallengeForElement(element, siteKeyService.value.siteKey, challengeOptions);
    return {
      resolveWith: resultPromise
    };
  }, [reCaptchaService, siteKeyService]));
  var trackedRequestCaptchaChallenge = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(element, options) {
      var taskResult;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return startTask( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
                var payload, failureInfo;
                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        _context.next = 2;
                        return requestCaptchaChallenge(element, options);

                      case 2:
                        payload = _context.sent;

                        if (!isSuccessful(payload)) {
                          _context.next = 7;
                          break;
                        }

                        return _context.abrupt("return", {
                          succeed: {
                            payload: payload
                          }
                        });

                      case 7:
                        failureInfo = isException(payload) ? {
                          probabilityOfBug: ProbabilityOfBug.GUARANTEED,
                          loggablePayload: payload.exception
                        } : {};
                        return _context.abrupt("return", {
                          fail: _objectSpread({
                            payload: payload
                          }, failureInfo)
                        });

                      case 9:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee);
              })));

            case 2:
              taskResult = _context2.sent;

              if (!(taskResult.fail !== undefined)) {
                _context2.next = 7;
                break;
              }

              return _context2.abrupt("return", taskResult.fail.payload);

            case 7:
              return _context2.abrupt("return", taskResult.succeed.payload);

            case 8:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function (_x, _x2) {
      return _ref.apply(this, arguments);
    };
  }(), [requestCaptchaChallenge, startTask]);
  return trackedRequestCaptchaChallenge;
};

/**
 * @deprecated Use {@link useCaptchaChallengeEventHandler} or ${@link useRequestCaptchaChallenge} instead.
 *
 * @param fetchSiteKeyProgress Will be called when this hook needs to retrieve a reCAPTCHA site key.
 * @param onReCaptchaChallengeCallback A callback that get's called after retrieving a reCAPTCHA result
 * @param initializeOptions Customizable settings for this hook
 * @returns A callback that you can pass into an event handling prop (like the onClick of a button)
 */
export var useCaptchaChallengeCallback = function useCaptchaChallengeCallback(fetchSiteKeyProgress, onReCaptchaChallengeCallback, loadReCaptchaOptions) {
  var challenge = useRequestCaptchaChallenge(fetchSiteKeyProgress, loadReCaptchaOptions);
  var callbackWithChallenge = useCallback( /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(event) {
      var token;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return challenge(event.target);

            case 2:
              token = _context3.sent;
              _context3.next = 5;
              return onReCaptchaChallengeCallback(token, event);

            case 5:
              return _context3.abrupt("return", _context3.sent);

            case 6:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function (_x3) {
      return _ref3.apply(this, arguments);
    };
  }(), [challenge, onReCaptchaChallengeCallback]);
  return callbackWithChallenge;
};

/**
 * A ref callback hook that instruments element(s) with reCAPTCHA.
 *
 * @param element The element you want to show a CAPTCHA challenge next to (if you have a modal available)
 * @param onReCaptchaTokenResult A callback that get's called after retrieving a reCAPTCHA result
 * @param initializeOptions Customizable settings for this hook
 * @returns A ref callback. Pass this into the element that you want to be instrumented with reCAPTCHA.
 */
export var useCaptchaChallengeEventHandler = function useCaptchaChallengeEventHandler(fetchSiteKeyProgress, onReCaptchaChallengeCallback) {
  var _ref4 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var _ref4$eventType = _ref4.eventType,
      eventType = _ref4$eventType === void 0 ? 'click' : _ref4$eventType,
      options = _objectWithoutProperties(_ref4, ["eventType"]);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      reCaptchaElement = _useState2[0],
      setReCaptchaElement = _useState2[1];

  var alreadyRunningReCaptchaState = useRef(false);
  var challenge = useRequestCaptchaChallenge(fetchSiteKeyProgress, options);
  useEffect(function () {
    if (reCaptchaElement === null) {
      return;
    } // TODO: Give event a more specific type depending on the event type string


    var eventListener = /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(event) {
        var result;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                if (!alreadyRunningReCaptchaState.current) {
                  _context4.next = 4;
                  break;
                }

                _context4.next = 3;
                return challenge(reCaptchaElement);

              case 3:
                return _context4.abrupt("return");

              case 4:
                alreadyRunningReCaptchaState.current = true;
                _context4.next = 7;
                return challenge(reCaptchaElement);

              case 7:
                result = _context4.sent;
                alreadyRunningReCaptchaState.current = false;
                _context4.next = 11;
                return onReCaptchaChallengeCallback(result, event);

              case 11:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }));

      return function eventListener(_x4) {
        return _ref5.apply(this, arguments);
      };
    }();

    reCaptchaElement.addEventListener(eventType, eventListener);
    return function () {
      reCaptchaElement.removeEventListener(eventType, eventListener);
    };
  }, [reCaptchaElement, challenge, onReCaptchaChallengeCallback, eventType]);
  return setReCaptchaElement;
};